import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api, sessionStore, setUnauthenticatedHandler } from "./api";
import type { Mode, Session, Tenant, User } from "./types";

/**
 * Session state for the whole application.
 *
 * The sandbox/live switch lives here rather than in a page, because it changes what
 * every screen shows and must survive navigation. It defaults to sandbox: a mistake
 * that shows test data is harmless, one that exposes live figures is not.
 */

interface AuthValue {
  user: User | null;
  tenant: Tenant | null;
  mode: Mode;
  setMode: (mode: Mode) => void;
  isOperator: boolean;
  ready: boolean;
  signIn: (email: string, password: string) => Promise<Session>;
  register: (input: RegisterInput) => Promise<Session>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

export interface RegisterInput {
  company_name: string;
  trading_name?: string;
  registration_number?: string;
  contact_name: string;
  email: string;
  phone?: string;
  password: string;
}

const AuthContext = createContext<AuthValue | null>(null);

const MODE_KEY = "bushveld.mode";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [ready, setReady] = useState(false);
  const [mode, setModeState] = useState<Mode>(() => {
    try {
      return localStorage.getItem(MODE_KEY) === "live" ? "live" : "sandbox";
    } catch {
      return "sandbox";
    }
  });

  const setMode = useCallback((next: Mode) => {
    setModeState(next);
    try {
      localStorage.setItem(MODE_KEY, next);
    } catch {
      /* the choice simply will not persist */
    }
  }, []);

  const applySession = useCallback((session: Session) => {
    sessionStore.write(session);
    setUser(session.user);
    setTenant(session.tenant ?? null);
  }, []);

  const clear = useCallback(() => {
    sessionStore.clear();
    setUser(null);
    setTenant(null);
  }, []);

  // A 401 that cannot be refreshed anywhere in the app returns the user to sign-in.
  useEffect(() => {
    setUnauthenticatedHandler(clear);
  }, [clear]);

  const refreshProfile = useCallback(async () => {
    try {
      const profile = await api.get<{ user: User; tenant: Tenant | null }>("/api/dashboard/me");
      setUser(profile.user);
      setTenant(profile.tenant);
    } catch {
      clear();
    }
  }, [clear]);

  // Restore a stored session at start-up, verifying it against the server rather than
  // trusting the cached copy: the account may have been suspended since.
  useEffect(() => {
    const stored = sessionStore.read();
    if (!stored) {
      setReady(true);
      return;
    }
    setUser(stored.user);
    setTenant(stored.tenant ?? null);
    refreshProfile().finally(() => setReady(true));
  }, [refreshProfile]);

  // A tenant that cannot transact live must never be left looking at a live view.
  useEffect(() => {
    if (mode === "live" && tenant && !tenant.can_transact_live) {
      setMode("sandbox");
    }
  }, [mode, tenant, setMode]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const session = await api.postPublic<Session>("/api/dashboard/auth/login", { email, password });
      applySession(session);
      return session;
    },
    [applySession],
  );

  const register = useCallback(
    async (input: RegisterInput) => {
      const session = await api.postPublic<Session>("/api/dashboard/auth/register", input);
      applySession(session);
      return session;
    },
    [applySession],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post("/api/dashboard/auth/logout");
    } catch {
      // Signing out locally must succeed even if the server call does not; leaving a
      // user apparently signed in after they asked to leave is the worse failure.
    }
    clear();
  }, [clear]);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      tenant,
      mode,
      setMode,
      isOperator: Boolean(user?.is_platform_operator),
      ready,
      signIn,
      register,
      signOut,
      refreshProfile,
    }),
    [user, tenant, mode, setMode, ready, signIn, register, signOut, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside an AuthProvider");
  return value;
}
